/**
 * copilotErrorMessages — accesseurs purs des erreurs du copilote.
 *
 * Contrat rest (CopilotAgent, ErrorDetails) : l'événement SSE `error` et le corps JSON d'une réponse HTTP
 * d'erreur portent un `code` STABLE (`ai.pillar.notConfigured`, `ai.pillar.disabled`, `ai.budget.exhausted`,
 * `ai.connection.missing`, `ai.provider.unsupported`, `ai.provider.error`, `ai.internal`…) et un `message`
 * technique en anglais (aussi dans les journaux du serveur). Ce `message` n'est JAMAIS affiché : on traduit le
 * `code`, et tout code inconnu (ou une donnée illisible, ou un ancien serveur qui envoie du texte brut) retombe
 * sur le message générique traduit.
 *
 * Fonctions pures, sans React ni react-intl : le tiroir passe une fermeture `t` (la règle i18n interdit les
 * clés composées dans le JSX, d'où ces accesseurs de niveau module avec des clés littérales).
 */

export type CopilotTranslator = (id: string) => string;

/** Lit le `code` d'un JSON `{ code, ... }` (données SSE `error` ou corps HTTP) ; null si absent ou illisible. */
export function parseCopilotErrorCode(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object') {
      const code = (parsed as { code?: unknown }).code;
      if (typeof code === 'string' && code.trim() !== '') {
        return code.trim();
      }
    }
  } catch {
    // texte brut d'un ancien serveur : traité comme un code inconnu
  }
  return null;
}

/** Libellé traduit d'un code d'erreur du copilote ; message générique pour un code absent ou inconnu. */
export function copilotErrorText(t: CopilotTranslator, code: string | null | undefined): string {
  switch (code) {
    case 'ai.pillar.notConfigured':
      return t('copilot.error.notConfigured');
    case 'ai.pillar.disabled':
      return t('copilot.error.disabled');
    case 'ai.budget.exhausted':
      return t('copilot.error.budgetExhausted');
    case 'ai.connection.missing':
    case 'ai.provider.unsupported':
      return t('copilot.error.connectionMissing');
    case 'ai.provider.error':
      return t('copilot.error.provider');
    default:
      return t('copilot.error.generic');
  }
}
